import { formatYen } from "../../domain/format";
import { formatHourlyRange } from "../../domain/hourlySales";
import type { Summary, Workspace } from "../../domain/types";
import type { ScreenName } from "../uiTypes";

type HomeScreenProps = {
  summary: Summary;
  workspace: Workspace;
  onNavigate: (screen: ScreenName) => void;
};

export function HomeScreen({ summary, workspace, onNavigate }: HomeScreenProps) {
  const cards = [
    { label: "本日の販売", value: `${summary.soldToday}枚`, meta: `現在 ${summary.currentHour.soldCount}/${summary.currentHour.limit}枚` },
    { label: "全体販売", value: `${summary.totalSold}枚`, meta: `払い戻し ${summary.totalRefunded}枚` },
    { label: "未使用", value: `${summary.issuedCount}枚`, meta: `使用済み ${summary.usedCount}枚` },
    { label: "販売金額", value: formatYen(summary.grossSalesYen), meta: `${summary.checkoutCount}件の販売` },
    { label: "払い戻し", value: formatYen(summary.refundsYen), meta: `${summary.totalRefunded}枚` },
    { label: "最終金額", value: formatYen(summary.netSalesYen), meta: `販売金額 ${formatYen(summary.grossSalesYen)}から控除` },
  ];

  return (
    <div className="screen home-screen">
      <section className="welcome-panel">
        <div>
          <p className="eyebrow">運用回 {workspace.sequence}・{summary.dayNumber}日目</p>
          <h2>受付を始めます</h2>
          <p>現金と木製チケットを確認し、操作を選んでください。</p>
        </div>
        <div className="home-primary-actions">
          <button type="button" className="launch-button launch-button--sales" onClick={() => onNavigate("sales")}>
            <span aria-hidden="true">¥</span>
            <strong>販売窓口</strong>
            <small>人数・販売・発番</small>
          </button>
          <button type="button" className="launch-button launch-button--entry" onClick={() => onNavigate("admission")}>
            <span aria-hidden="true">✓</span>
            <strong>入場受付</strong>
            <small>券番号・使用登録</small>
          </button>
        </div>
      </section>
      <section aria-labelledby="summary-title">
        <div className="section-heading">
          <div>
            <p className="eyebrow">現在の状況</p>
            <h2 id="summary-title">販売・会計概要</h2>
          </div>
          <p className="quiet">表示中の領域：{workspace.kind === "DEV" ? "テストデータ" : "本番データ"}</p>
        </div>
        <div className="metric-grid">
          {cards.map((card) => (
            <article className="metric-card" key={card.label}>
              <span>{card.label}</span>
              <strong>{card.value}</strong>
              <small>{card.meta}</small>
            </article>
          ))}
        </div>
        <div className="hourly-sales-panel">
          <div className="section-heading compact-heading">
            <div><p className="eyebrow">各HH:00から1時間</p><h3>時間別販売状況</h3></div>
            <p className="quiet">基準を超えた時間は超過枚数を表示します。</p>
          </div>
          <div className="hourly-sales-grid">
            {summary.hourlySales.map((hour) => (
              <article
                className={`hourly-sales-item ${hour.overage > 0 ? "hourly-sales-item--over" : ""} ${hour.hourStartMs === summary.currentHour.hourStartMs ? "hourly-sales-item--current" : ""}`}
                key={hour.hourStartMs}
              >
                <span>{formatHourlyRange(hour.hour)}</span>
                <strong>{hour.soldCount}/{hour.limit}枚</strong>
                <small>{hour.overage > 0 ? `${hour.overage}枚超過` : "基準内"}</small>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="home-secondary-actions" aria-label="その他の操作">
        <button type="button" onClick={() => onNavigate("accounting")}><strong>会計</strong><span>販売・払い戻し・最終金額</span></button>
        <button type="button" onClick={() => onNavigate("records")}><strong>記録</strong><span>券・販売・履歴</span></button>
        <button type="button" onClick={() => onNavigate("admin")}><strong>管理</strong><span>営業・テスト・リセット</span></button>
      </section>
    </div>
  );
}
